import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './LinkDesktop.module.css';

type LinkState = 'idle' | 'posting' | 'success' | 'error';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function LinkDesktop() {
  const { currentUser } = useAuth();
  const query = useQuery();
  const [state, setState] = useState<LinkState>('idle');
  const [message, setMessage] = useState<string>('');

  const port = query.get('port') || '';
  const token = query.get('token') || '';
  const userName = query.get('name') || '';

  const portNum = Number(port);
  const isPortValid = !!port && !Number.isNaN(portNum) && portNum >= 1 && portNum <= 65535;

  const manualLinkUrl =
    currentUser && isPortValid
      ? `http://127.0.0.1:${portNum}/link?firebaseUid=${encodeURIComponent(currentUser.uid)}&token=${encodeURIComponent(
          token
        )}${userName ? `&userName=${encodeURIComponent(userName)}` : ''}`
      : '';

  useEffect(() => {
    if (!currentUser) return;

    if (!isPortValid) {
      setState('error');
      setMessage('Missing or invalid port. Please restart linking from the desktop app.');
      return;
    }

    if (!token) {
      setState('error');
      setMessage('Missing token. Please restart linking from the desktop app.');
      return;
    }

    const uid = currentUser.uid;
    if (!uid) {
      setState('error');
      setMessage('Could not read your account ID. Please log out and log back in.');
      return;
    }

    let cancelled = false;

    async function postLink() {
      try {
        setState('posting');
        setMessage(userName ? `Linking ${userName} to your desktop app…` : 'Linking to your desktop app…');

        const hosts = ['127.0.0.1', 'localhost'];
        let resp: Response | null = null;
        let lastError: unknown = null;

        for (const host of hosts) {
          try {
            resp = await fetch(`http://${host}:${portNum}/link`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ firebaseUid: uid, token, userName: userName || undefined }),
            });
            break;
          } catch (e: unknown) {
            lastError = e;
          }
        }

        if (!resp) {
          throw lastError;
        }

        const text = await resp.text().catch(() => '');

        if (cancelled) return;

        if (!resp.ok) {
          setState('error');
          setMessage(text || 'Desktop app rejected the link request. Make sure the desktop app is open.');
          return;
        }

        setState('success');
        setMessage('Linked successfully. You can now return to the desktop app.');
      } catch (e: unknown) {
        if (cancelled) return;
        const errMsg = e instanceof Error ? e.message : 'Unknown error';
        setState('error');
        setMessage(
          `Could not reach the desktop app on 127.0.0.1/localhost:${portNum}. Make sure the desktop app is open and try again.\n\nFirebase UID: ${uid}\n\n${errMsg}`
        );
      }
    }

    postLink();

    return () => {
      cancelled = true;
    };
  }, [currentUser, port, token]);

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Link Desktop | My People Database</title>
        <meta name="description" content="Link your My People Database desktop app to your web account." />
      </Helmet>

      <div className={styles.card}>
        <h1 className={styles.title}>Link Desktop</h1>

        <p className={styles.body}>
          This will link your desktop installation to your My People Database account.
        </p>

        {userName && (
          <p className={styles.body}>
            Linking for: <strong>{userName}</strong>
          </p>
        )}

        <div
          className={
            state === 'success'
              ? styles.statusSuccess
              : state === 'error'
                ? styles.statusError
                : styles.statusInfo
          }
        >
          {message || 'Waiting…'}
        </div>

        {state === 'success' && (
          <p className={styles.body}>You can close this tab.</p>
        )}

        {state === 'error' && (
          <>
            <p className={styles.body}>
              If this keeps failing, click “Link My People Database account” in the desktop app again.
            </p>
            {!!currentUser?.uid && (
              <>
                <p className={styles.urlLabel}>Your Firebase UID (to copy/paste):</p>
                <div className={styles.urlBox}>{currentUser.uid}</div>
              </>
            )}
          </>
        )}

        {isPortValid && (
          <>
            <p className={styles.urlLabel}>
              If linking fails, visit this address to confirm the desktop link server is reachable, then try again:
            </p>
            <div className={styles.urlBox}>{`http://127.0.0.1:${port}/health`}</div>

            {!!manualLinkUrl && (
              <>
                <p className={styles.urlLabel}>
                  If your browser blocks background requests to localhost (for example by an ad blocker / privacy extension), use this manual link:
                </p>
                <div className={styles.urlBox}>
                  <a href={manualLinkUrl} target="_blank" rel="noreferrer">
                    {manualLinkUrl}
                  </a>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
